
import React, { useEffect, useRef } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { cn } from '@/lib/utils';
import { Dumbbell, Footprints, Award, HeartPulse, Timer, PersonStanding, Building, CreditCard, User, Users, ListCheck } from 'lucide-react';

interface FeatureCardProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  index: number;
}

function FeatureCard({ icon, title, description, index }: FeatureCardProps) {
  return (
    <div 
      className="animated-element flex flex-col items-center text-center p-6 rounded-3xl transition-all duration-300 hover:scale-105"
      style={{ animationDelay: `${index * 0.1}s` }}
    >
      <div className={cn(
        "w-16 h-16 rounded-2xl flex items-center justify-center mb-5 shadow-lg",
        index % 3 === 0 ? "bg-gradient-to-tr from-blue-500 to-blue-400 text-white" :
        index % 3 === 1 ? "bg-gradient-to-tr from-purple-500 to-purple-400 text-white" :
        "bg-gradient-to-tr from-pink-500 to-pink-400 text-white"
      )}>
        {icon}
      </div>
      <h3 className="text-xl font-bold mb-3">{title}</h3>
      <p className="text-muted-foreground max-w-xs">{description}</p>
    </div>
  );
}

export function Features() {
  const { t } = useLanguage();
  const featuresRef = useRef<HTMLDivElement>(null);
  
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const elements = entry.target.querySelectorAll('.animated-element');
            elements.forEach((el, i) => {
              setTimeout(() => {
                el.classList.add('appear');
              }, i * 100);
            });
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );
    
    if (featuresRef.current) {
      observer.observe(featuresRef.current);
    }
    
    return () => observer.disconnect();
  }, []);

  const features = [
    {
      icon: <Dumbbell className="w-8 h-8" />,
      title: t('features.smartScheduling.title'),
      description: t('features.smartScheduling.description'),
    },
    {
      icon: <Users className="w-8 h-8" />,
      title: t('features.studentManagement.title'),
      description: t('features.studentManagement.description'),
    },
    {
      icon: <ListCheck className="w-8 h-8" />,
      title: t('features.categoriesManagement.title'),
      description: t('features.categoriesManagement.description'),
    },
    {
      icon: <Award className="w-8 h-8" />,
      title: t('features.automaticInvoices.title'),
      description: t('features.automaticInvoices.description'),
    },
    {
      icon: <User className="w-8 h-8" />,
      title: t('features.studentAccounts.title'),
      description: t('features.studentAccounts.description'),
    },
    {
      icon: <Users className="w-8 h-8" />,
      title: t('features.parentAccounts.title'),
      description: t('features.parentAccounts.description'),
    },
    {
      icon: <Timer className="w-8 h-8" />,
      title: t('features.onlinePayments.title'),
      description: t('features.onlinePayments.description'),
    },
    
    {
      icon: <PersonStanding className="w-8 h-8" />,
      title: t('features.aiSummaries.title'),
      description: t('features.aiSummaries.description'),
    },
    {
      icon: <Building className="w-8 h-8" />,
      title: t('features.schoolAccounts.title'),
      description: t('features.schoolAccounts.description'),
    },
  ];

  return (
    <section id="features" className="py-20 relative overflow-hidden" ref={featuresRef}>
      {/* Modern gradient background */}
      <div className="absolute inset-0 bg-gradient-to-b from-white via-blue-50 to-white -z-10" />
      
      <div className="container px-4 md:px-6">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="animated-element">
            <span className="inline-block bg-gradient-to-r from-blue-500 to-purple-500 text-white px-4 py-1 rounded-full text-sm font-medium mb-6">
              {t('common.getStarted')}
            </span>
          </div>
          <h2 className="animated-element text-3xl md:text-4xl font-bold mb-6 bg-gradient-to-r from-blue-600 via-purple-600 to-blue-600 bg-clip-text text-transparent">
            {t('features.title')}
          </h2>
          <p className="animated-element text-xl text-muted-foreground">
            {t('features.subtitle')}
          </p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-16">
          {features.map((feature, index) => (
            <FeatureCard
              key={index}
              icon={feature.icon}
              title={feature.title}
              description={feature.description}
              index={index}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
